/**
 * The cities screen: every city in one table.
 *
 * Built because Mark played ninety-six turns and could not tell what his cities were
 * doing without opening each one in turn. That is the failure mode a 4X interface has
 * to avoid above all others — the information exists, the player simply cannot reach
 * it — and it gets worse with every city founded.
 *
 * One row per city, one column per question a player actually asks: how big, growing
 * or not, building what and when does it land, earning what, and is it in trouble.
 */

import type { Engine } from '../core/engine.js';
import { empireResources } from './resource-text.js';
import { cityYield, foodSurplus, growthThreshold } from '../core/cities/index.js';
import { dragFraction, effectiveYield, mood } from '../core/cities/economy.js';
import { isAtSizeCap, itemCost, itemName, sizeCap } from '../core/cities/production.js';
import { citiesOf } from '../core/cities/index.js';
import type { City } from '../core/state/types.js';

export interface CitiesScreen {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  refresh(): void;
}

export interface CitiesScreenOptions {
  getEngine: () => Engine;
  /** Open one city's own screen. */
  onOpenCity: (cityId: number) => void;
  /** Put the map on a city without opening anything. */
  onGoToCity: (cityId: number) => void;
}

export function createCitiesScreen(options: CitiesScreenOptions): CitiesScreen {
  const root = document.createElement('div');
  root.className = 'cities-screen';
  root.hidden = true;
  root.innerHTML = `
    <div class="cities-panel">
      <header>
        <h2>Cities</h2>
        <div class="cs-sub" id="cts-sub"></div>
        <button id="cts-close" title="Close (Esc)">Close</button>
      </header>
      <div class="cts-resources" id="cts-resources"></div>
      <div class="cts-body"><table id="cts-table"></table></div>
    </div>
  `;
  document.body.append(root);

  const $ = <T extends HTMLElement>(id: string): T => root.querySelector(`#${id}`) as T;

  /** What a city is building and when it will land, or why it is not building. */
  function buildingCell(engine: Engine, city: City): { text: string; flag: string } {
    const { state, content } = engine;
    if (mood(state, content, city).disorder) return { text: 'in disorder', flag: 'bad' };

    const head = city.queue[0];
    if (!head) return { text: 'nothing', flag: 'idle' };

    const production = effectiveYield(state, content, city).production;
    const remaining = itemCost(content, head, state.civs[city.owner]) - city.productionStore;
    if (production <= 0) return { text: `${itemName(content, head)} — no production`, flag: 'bad' };

    const turns = Math.max(1, Math.ceil(remaining / production));
    return { text: `${itemName(content, head)} · ${turns}t`, flag: turns <= 1 ? 'soon' : '' };
  }

  function growthCell(engine: Engine, city: City): { text: string; flag: string } {
    const { state, content } = engine;
    if (isAtSizeCap(content, city)) return { text: 'at its limit', flag: 'idle' };

    const surplus = foodSurplus(state, content, city);
    if (surplus < 0) return { text: 'starving', flag: 'bad' };
    if (surplus === 0) return { text: 'static', flag: 'idle' };

    const turns = Math.ceil((growthThreshold(content, city.population) - city.foodStore) / surplus);
    return { text: `+${surplus} · ${turns}t`, flag: '' };
  }

  function refresh(): void {
    const engine = options.getEngine();
    const { state, content } = engine;
    const cities = citiesOf(state, engine.activeCiv);

    const totals = cities.reduce(
      (acc, city) => {
        const net = effectiveYield(state, content, city);
        acc.population += city.population;
        acc.production += net.production;
        acc.gold += net.gold;
        return acc;
      },
      { population: 0, production: 0, gold: 0 },
    );

    $('cts-sub').textContent =
      `${cities.length} ${cities.length === 1 ? 'city' : 'cities'} · ${totals.population} citizens · ` +
      `${totals.production} production · ${totals.gold} gold per turn`;

    // Strategic resources, empire-wide (ADR-0063): what you have, what lies unjoined in
    // your land, what you lack and what research would reveal.
    const words = { have: 'joined', traded: 'traded in', unjoined: 'in your land — needs a road', lacking: 'none in your land', hidden: 'not yet revealed' } as const;
    $('cts-resources').innerHTML =
      '<span class="dim">Strategic resources:</span> ' +
      empireResources(state, content, engine.activeCiv)
        .map(
          (r) =>
            `<span class="res-chip ${r.status}" title="${r.status === 'hidden' ? `Revealed by ${escapeHtml(r.revealedBy ?? '?')}` : words[r.status]}${r.usedBy.length ? ` · needed for ${escapeHtml(r.usedBy.join(', '))}` : ' · nothing needs it yet'}">${escapeHtml(r.name)} <span class="dim">${r.status === 'hidden' ? `after ${escapeHtml(r.revealedBy ?? '?')}` : words[r.status]}</span></span>`,
        )
        .join('');

    if (cities.length === 0) {
      $('cts-table').innerHTML = '<tr><td class="dim">No cities yet. Found one with a settler.</td></tr>';
      return;
    }

    const header =
      '<tr><th>City</th><th>Size</th><th>Growth</th><th>Building</th><th>Prod</th><th>Gold</th><th>Mood</th><th>Distance</th><th></th></tr>';

    const rows = cities
      .map((city) => {
        const net = effectiveYield(state, content, city);
        const raw = cityYield(state, content, city);
        const feeling = mood(state, content, city);
        const drag = dragFraction(state, content, city);
        const build = buildingCell(engine, city);
        const growth = growthCell(engine, city);

        const moodText = feeling.disorder
          ? 'disorder'
          : feeling.discontented > 0
            ? `${feeling.discontented} discontented`
            : 'content';
        const moodFlag = feeling.disorder ? 'bad' : feeling.discontented > 0 ? 'soon' : '';

        return `<tr data-city="${city.id}">
          <td class="cts-name">${escapeHtml(city.name)}${drag === 0 ? ' <span class="dim">★</span>' : ''}</td>
          <td>${city.population}/${sizeCap(content, city)}</td>
          <td class="${growth.flag}">${escapeHtml(growth.text)}</td>
          <td class="${build.flag}">${escapeHtml(build.text)}</td>
          <td>${net.production}</td>
          <td>${net.gold}${drag > 0 ? `<span class="dim"> of ${raw.gold}</span>` : ''}</td>
          <td class="${moodFlag}">${escapeHtml(moodText)}</td>
          <td class="dim">${drag > 0 ? `−${Math.round(drag * 100)}%` : 'capital'}</td>
          <td><button data-open="${city.id}">Open</button></td>
        </tr>`;
      })
      .join('');

    $('cts-table').innerHTML = header + rows;

    for (const button of root.querySelectorAll<HTMLButtonElement>('button[data-open]')) {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        options.onOpenCity(Number(button.dataset['open']));
      });
    }
    for (const row of root.querySelectorAll<HTMLTableRowElement>('tr[data-city]')) {
      row.addEventListener('click', () => {
        // Clicking the row takes you there; the button opens the city. Two different
        // intentions that both start with "that one".
        options.onGoToCity(Number(row.dataset['city']));
        close();
      });
    }
  }

  function open(): void {
    root.hidden = false;
    refresh();
  }

  function close(): void {
    root.hidden = true;
  }

  $('cts-close').addEventListener('click', close);
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  return {
    open,
    close,
    get isOpen(): boolean {
      return !root.hidden;
    },
    refresh,
  };
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
